import {
  createRef,
  type FC,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from "react";

import { clsx } from "@alpacahq/ui/old";

interface ToggleButtonProps {
  name: string;
  isSelected: boolean;
  onClick: () => void;
  disabled?: boolean;
  theme?: {
    [key: string]: {
      default: string;
      disabled: string;
      selected: string;
      highlight: string;
    };
  };
}

export const ToggleButton = forwardRef<HTMLDivElement, ToggleButtonProps>(
  ({ name, isSelected, onClick, disabled, theme }, ref) => {
    let buttonClass = clsx(
      "z-10 flex h-full cursor-pointer items-center justify-center rounded-lg bg-transparent px-4 font-medium text-sm transition-all duration-400 ease-in-out",
      isSelected && !disabled && "text-zinc-800",
      !isSelected && !disabled && "text-zinc-500",
      disabled && "cursor-not-allowed text-zinc-500/50",
    );

    if (theme?.[name]) {
      const themeClass = isSelected
        ? theme[name].selected
        : disabled
          ? theme[name].disabled
          : theme[name].default;

      buttonClass = clsx(buttonClass, themeClass);
    }

    return (
      <div
        ref={ref}
        className={buttonClass}
        onClick={() => {
          if (!disabled) {
            onClick();
          }
        }}
      >
        {name}
      </div>
    );
  },
);

interface ToggleButtonGroupProps {
  options: string[];
  selectedOption: string;
  onOptionClick: (option: string) => void;
  disabledOptions?: string[];
  theme?: ToggleButtonProps["theme"];
}

export const ToggleButtonGroup: FC<ToggleButtonGroupProps> = ({
  options,
  selectedOption,
  onOptionClick,
  disabledOptions = [],
  theme,
}) => {
  const [highlightStyle, setHighlightStyle] = useState({});

  const refs = useRef(options.map(() => createRef<HTMLDivElement>()));

  useEffect(() => {
    refs.current = options.map((_, i) => refs.current[i] || createRef());
  }, [options]);

  useEffect(() => {
    const index = options.indexOf(selectedOption);

    if (index !== -1) {
      const element = refs.current[index]?.current;

      if (element) {
        setHighlightStyle({
          left: element.offsetLeft,
          width: element.offsetWidth,
          transition: "left 0.3s ease-in-out, width 0.3s ease-in-out",
        });
      }
    }
  }, [selectedOption, options]);

  return (
    <div className="relative top-0 right-0 flex h-10 w-fit justify-between rounded-lg bg-zinc-100">
      <div
        className={clsx(
          "absolute h-full rounded-lg bg-zinc-200",
          theme?.[selectedOption]?.highlight,
        )}
        style={highlightStyle}
      />
      {options.map((option, index) => (
        <ToggleButton
          key={option}
          ref={refs.current[index]}
          name={option}
          isSelected={option === selectedOption}
          onClick={() => onOptionClick(option)}
          disabled={disabledOptions.includes(option)}
          theme={theme}
        />
      ))}
    </div>
  );
};
